import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React from 'react';

import { BulkActionButton, BulkActionsBarProps } from './BulkActionsBar.props';

/** Exported so callers can filter it down by permission before passing it back. */
export const DEFAULT_ACTION_BUTTONS: readonly BulkActionButton[] = [
  { key: 'bulk_update', label: 'Bulk Update', icon: <EditIcon sx={{ fontSize: 16 }} /> },
  { key: 'add_note', label: 'Add Note', icon: <NoteAddIcon sx={{ fontSize: 16 }} /> },
  { key: 'assign_tech', label: 'Assign Tech', icon: <PersonAddIcon sx={{ fontSize: 16 }} /> },
  { key: 'schedule', label: 'Schedule', icon: <CalendarTodayIcon sx={{ fontSize: 16 }} /> },
  {
    key: 'update_status',
    label: 'Update Status',
    icon: <PublishedWithChangesIcon sx={{ fontSize: 16 }} />,
  },
  { key: 'open_tabs', label: 'Open in Tabs', icon: <OpenInNewIcon sx={{ fontSize: 16 }} /> },
];

const BUTTON_SX = {
  color: 'common.white',
  borderColor: 'divider',
  fontSize: '12px',
  textTransform: 'none' as const,
  px: 1.5,
  py: 0.5,
  opacity: 0.85,
  '&:hover': {
    borderColor: 'common.white',
    bgcolor: 'action.hover',
    opacity: 1,
  },
} as const;

export const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  count,
  onAction,
  onClear,
  actions,
}) => {
  if (count <= 0) return null;
  const actionButtons = actions ?? DEFAULT_ACTION_BUTTONS;

  return (
    <Box
      role="toolbar"
      aria-label="Bulk actions bar"
      sx={(theme) => ({
        position: 'fixed',
        bottom: 0,
        right: 0,
        left: 0,
        bgcolor: 'v4.sidebarActive',
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1.25,
        // App-bar layer: above the table and its sticky headers, but below
        // drawers (1200) and dialogs (1300). It previously sat at modal + 1,
        // which put it over every dialog opened from a list page — the column
        // picker and the region add/edit form both came up behind it.
        zIndex: theme.zIndex.appBar,
        boxShadow: theme.shadows[8],
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{count} selected</Typography>
        {actionButtons.map((btn) => (
          <Button
            key={btn.key}
            variant="outlined"
            size="small"
            startIcon={btn.icon}
            onClick={() => onAction(btn.key)}
            sx={BUTTON_SX}
          >
            {btn.label}
          </Button>
        ))}
      </Box>
      <Link
        component="button"
        onClick={onClear}
        sx={{
          color: 'common.white',
          opacity: 0.7,
          fontSize: '12px',
          cursor: 'pointer',
          textDecoration: 'underline',
          '&:hover': { color: 'common.white', opacity: 1 },
        }}
      >
        Clear Selection
      </Link>
    </Box>
  );
};
